import { IonAlert, IonBackButton, IonButtons, IonHeader, IonTitle, IonToolbar, useIonToast } from "@ionic/react";
import { GroupChannel } from "@sendbird/chat/groupChannel";
import { useSendbirdStateContext } from "@wearesweet/sendbird-uikit-react";
import { t } from "i18next";
import React, { useCallback, useEffect, useState } from "react";
import { debug } from "../../App";
import { SelectMembersModal } from "../../components/SelectMembersModal";
import { useViewport } from "../../utils/useViewport";
import { ChannelSettings as ChannelSettingsComponent } from "./components/ChannelSettings";

interface ConfirmEvent {
  confirm_type: string;
  on_confirmed: Function;
  confirmed_message: string;
}

export const ChannelSettings = (props: any) => {
  const { isMobile } = useViewport();
  const [confirmIsOpen, setConfirmIsOpen] = useState<boolean>(false);
  const [currentEvent, setCurrentEvent] = useState<ConfirmEvent | null>();
  const [addMembersIsOpen, setAddMembersIsOpen] = useState<boolean>(false);
  const [addMembersChannelUrl, setAddMembersChannelUrl] = useState<string>("");
  const globalStore = useSendbirdStateContext();
  const [present] = useIonToast();

  const receiveConfirm = useCallback((event: any) => {
    debug("Received confirm", event.detail);
    setCurrentEvent(event.detail);
    setConfirmIsOpen(true);
  }, []);

  const receiveAddToChannel = useCallback((event: any) => {
    debug("Received add to channel", event.detail);
    setAddMembersChannelUrl(event.detail.channel_url);
    setAddMembersIsOpen(true);
  }, []);

  useEffect(() => {
    window.addEventListener("sendbird.confirm", receiveConfirm);

    return () => {
      window.removeEventListener("sendbird.confirm", receiveConfirm);
    };
  }, [receiveConfirm]);

  useEffect(() => {
    window.addEventListener("sendbird.addMemberToChannel", receiveAddToChannel);

    return () => {
      window.removeEventListener("sendbird.addMemberToChannel", receiveAddToChannel);
    };
  }, [receiveAddToChannel]);

  return (
    <>
      {currentEvent ? (
        <IonAlert
          isOpen={confirmIsOpen}
          header={t("areYouSure") || ""}
          message={t(currentEvent!.confirm_type) || ""}
          buttons={[
            {
              text: t("cancel"),
              role: "cancel",
              cssClass: "text-black",
              handler: () => {
                setConfirmIsOpen(false);
              },
            },
            {
              text: t("yes"),
              cssClass: "text-report",
              handler: () => {
                currentEvent!.on_confirmed();
                present({
                  message: t(currentEvent!.confirmed_message) || "",
                  duration: 5000,
                  position: "bottom",
                });
                setConfirmIsOpen(false);
              },
            },
          ]}
        />
      ) : (
        ""
      )}

      <SelectMembersModal
        isOpen={addMembersIsOpen}
        next={t("Next")}
        title={t("addMembers")}
        onSelected={(selectedMembers: string[]) => {
          globalStore.stores.sdkStore.sdk.groupChannel.getChannel(addMembersChannelUrl).then((channel: GroupChannel) => {
            channel
              .inviteWithUserIds(selectedMembers)
              .then(() => {
                present({
                  message: t("membersAdded") || "",
                  duration: 5000,
                  position: "bottom",
                  color: "success",
                });

                setTimeout(() => {
                  window.dispatchEvent(new CustomEvent("sendbird.refreshMemberList"));
                }, 500);
              })
              .catch(() => {
                present({
                  message: t("errorAddingMembers") || "",
                  duration: 5000,
                  position: "bottom",
                  color: "danger",
                });
              });
          });
        }}
        except={async () => {
          debug("Getting channel", addMembersChannelUrl);
          const channel: GroupChannel = await globalStore.stores.sdkStore.sdk.groupChannel.getChannel(addMembersChannelUrl);
          return channel.members.map((member) => member.userId);
        }}
        onDidDismiss={() => {
          setAddMembersIsOpen(false);
        }}
      />

      {isMobile && (
        <IonHeader translucent={true}>
          <IonToolbar>
            <IonButtons slot={"start"}>
              <IonBackButton defaultHref={"/" + (isMobile ? "m" : "") + `chat/channels/${props.channelUrl}/`}></IonBackButton>
            </IonButtons>

            <IonTitle>{t("channelInfo")}</IonTitle>
          </IonToolbar>
        </IonHeader>
      )}

      <div style={{ height: "100vh" }}>
        <ChannelSettingsComponent channelUrl={props.channelUrl} onClose={props.onClose} />
      </div>
    </>
  );
};
